"use client";

import { useToast } from "@/app/hooks/use-toast";
import { authenticate } from "@/lib/actions/auth";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Input,
  Label,
} from "@zyno/ui";
import { Loader2 } from "lucide-react";
import { useState } from "react";

export default function LoginPage() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await authenticate(formData.email, formData.password);
      showToast("Inicio de sesión exitoso");
    } catch (error) {
      if (error && (error as Error).message === "NEXT_REDIRECT") {
        throw error;
      }
      showToast(
        `Hubo un problema al iniciar sesión, intenta nuevamente.`,
        "error"
      );
    } finally {
      setIsLoading(false);
      queryClient.invalidateQueries({ predicate: () => true });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-sm rounded-2xl shadow-xl border border-slate-100">
        <CardHeader className="flex flex-col items-center mb-2">
          <h1 className="text-2xl font-bold text-slate-900">Iniciar sesión</h1>
          <p className="text-sm text-slate-500 mt-1 text-center">
            Ingresa tus credenciales para continuar
          </p>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">
                Correo electrónico
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={formData.email}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, email: e.target.value }))
                }
                disabled={isLoading}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, password: e.target.value }))
                }
                disabled={isLoading}
                required
                className="mt-1"
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-2">
            <div className="flex justify-end w-full">
              <a
                href="/forgot-password"
                className="text-xs text-blue-700 hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </a>
            </div>
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full font-semibold"
              size="lg"
              variant="gradient"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Iniciando sesión...
                </span>
              ) : (
                "Iniciar sesión"
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
